import { BitmapText, Container, Text as PixiText } from 'pixi.js'
import { Slot, Spine } from '@esotericsoftware/spine-pixi'
import { IGodziBaseStore } from '../store/godziDropStore'
import { GodziDropSoundTypeV2 } from '../enum/GodziDropSoundTypeV2'
import { GodziDropAnimation } from '../enum/GodziDropAnimation'
import { playSpine } from '../../../util'
import { formatToCommaWithLang } from '../../../util/formatUtil'
import { registerSpaceBarEvent } from '../../../util/keyEventUtil'
import { sleep } from '../../../util/sleep'
import { showTapToContinueText } from '../../../util/tapAnyWhereUtil'
import { soundManager, SoundType } from '../../sound/SoundManager'
import { GodziDropResultType } from '../enum/GodziDropResultType'
import { GodziPrizeTierType } from '../enum/GodziPrizeTierType'
import { GodziSpinType } from '../enum/GodziSpinType'
import { StoreApi, UseBoundStore } from 'zustand'

export interface IGodziDropOption {
  store: UseBoundStore<StoreApi<IGodziBaseStore>>
  loadGodziResource: () => Promise<void>
  prepareGodziSpinData: () => void
  onStartRatioPrizeCallback: () => void
  onStartFreeGameCallback: () => void
  onStartFreeSpinCallback: () => void
  onCloseTotalWinCallback: () => void
}

export class GodziDropService {
  private store: UseBoundStore<StoreApi<IGodziBaseStore>>
  private loadGodziResource: () => Promise<void>
  public prepareGodziSpinData: () => void
  private onStartRatioPrizeCallback: () => void
  private onStartFreeGameCallback: () => void
  private onStartFreeSpinCallback: () => void
  private onCloseTotalWinCallback: () => void

  private godziWrapper: Container
  private godziSpine: Spine
  private tapHintText: PixiText

  constructor(options: IGodziDropOption) {
    this.store = options.store
    this.loadGodziResource = options.loadGodziResource
    this.prepareGodziSpinData = options.prepareGodziSpinData
    this.onStartRatioPrizeCallback = options.onStartRatioPrizeCallback
    this.onStartFreeGameCallback = options.onStartFreeGameCallback
    this.onStartFreeSpinCallback = options.onStartFreeSpinCallback
    this.onCloseTotalWinCallback = options.onCloseTotalWinCallback
  }

  public initGodziDropWrapper(container: Container) {
    this.godziWrapper = container
  }

  private startGodziSpin(godziSpinType: GodziSpinType) {
    this.godziWrapper.onpointertap = null

    soundManager.switchBGM(SoundType.bgm)
    soundManager.playBGM()

    this.godziWrapper.removeChildren()
    this.godziSpine.destroy()
    setTimeout(() => {
      if (godziSpinType === GodziSpinType.freeGame) this.onStartFreeGameCallback()
      else if (godziSpinType === GodziSpinType.freeSpin) this.onStartFreeSpinCallback()
    }, 800)
  }

  private exitGodziDrop() {
    this.godziWrapper.onpointertap = null

    this.godziSpine.destroy()
    if (this.tapHintText) this.tapHintText.destroy()
    this.godziWrapper.removeChildren()
    this.store.setState({
      effectiveStake: 0,
      type: GodziDropResultType.None,
      prize: 0,
      payout: 0,
      godziSpinType: GodziSpinType.none,
    })
    soundManager.switchBGM(SoundType.bgm)
    soundManager.playBGM()

    this.onCloseTotalWinCallback()
  }

  public async enterRatioPrize() {
    await this.playEnterPrizeAnimation()

    const textSlot = this.godziSpine.skeleton.findSlot('nb') as Slot
    const multiPrize = this.store.getState().prize
    const multiText = new BitmapText(`X${multiPrize}`, { fontName: 'GodziFont', fontSize: 30 })
    multiText.anchor.set(0.5, 2.5)
    const delayForSlotMs = 50

    setTimeout(() => {
      this.godziSpine.addSlotObject(textSlot, multiText)
    }, delayForSlotMs)
    soundManager.play(GodziDropSoundTypeV2.enterPrize)
    await playSpine(this.godziSpine, GodziDropAnimation.ratioPrize)
    this.onStartRatioPrizeCallback()
  }

  public async enterFreeGamePrize() {
    await this.playEnterPrizeAnimation()
    soundManager.play(GodziDropSoundTypeV2.enterPrize)
    await playSpine(this.godziSpine, GodziDropAnimation.freeGamePrize)
    this.startGodziSpin(GodziSpinType.freeGame)
    this.store.setState({ godziSpinType: GodziSpinType.freeGame })
  }

  public async enterFreeSpinPrize() {
    await this.playEnterPrizeAnimation()
    const textSlot = this.godziSpine.skeleton.findSlot('nb') as Slot
    const freeSpinCount = this.store.getState().freeSpinCount
    const freeSpinCountText = new BitmapText(freeSpinCount.toString(), { fontName: 'GodziFont', fontSize: 27 })
    freeSpinCountText.anchor.set(0.8, 2)
    const delayForSlotMs = 50
    setTimeout(() => {
      this.godziSpine.addSlotObject(textSlot, freeSpinCountText)
    }, delayForSlotMs)
    soundManager.play(GodziDropSoundTypeV2.enterPrize)
    await playSpine(this.godziSpine, GodziDropAnimation.freeSpinPrize)
    this.startGodziSpin(GodziSpinType.freeSpin)
    this.store.setState({ godziSpinType: GodziSpinType.freeSpin })
  }

  private async playEnterPrizeAnimation() {
    await this.loadGodziResource()
    soundManager.switchBGM(GodziDropSoundTypeV2.bgm)
    soundManager.playBGM()
    this.godziSpine = Spine.from('godziDrop.skel', 'godziDrop.atlas')
    this.godziWrapper.addChild(this.godziSpine)
    soundManager.play(GodziDropSoundTypeV2.godziRoar)

    await playSpine(this.godziSpine, GodziDropAnimation.open)
    soundManager.stopBGM()

    await this.showPrizeTierAnimation()
  }

  private async showPrizeTierAnimation() {
    const prizeTier = this.store.getState().prizeTier

    switch (prizeTier) {
      case GodziPrizeTierType.Golden:
        soundManager.play(GodziDropSoundTypeV2.goldenEgg)
        await playSpine(this.godziSpine, GodziDropAnimation.goldLevelIntro)
        break
      case GodziPrizeTierType.Vip:
        soundManager.play(GodziDropSoundTypeV2.vipEgg)
        await playSpine(this.godziSpine, GodziDropAnimation.vipLevelIntro)
        break
      default:
        soundManager.play(GodziDropSoundTypeV2.silverEgg)
        await playSpine(this.godziSpine, GodziDropAnimation.silverLevelIntro)
        break
    }
  }

  public async showGodziTotalWin() {
    const { payout, type } = this.store.getState()
    if (type !== GodziDropResultType.RatioPrize) {
      await sleep(500)
    }

    this.godziSpine = Spine.from('godziDrop.skel', 'godziDrop.atlas')
    this.godziWrapper.addChild(this.godziSpine)
    if (payout > 0) {
      soundManager.switchBGM(GodziDropSoundTypeV2.bgm)
      soundManager.playBGM()
      const textSlot = this.godziSpine.skeleton.findSlot('nb') as Slot
      playSpine(this.godziSpine, GodziDropAnimation.totalWin, true)
      soundManager.play(GodziDropSoundTypeV2.totalWin)

      const payoutText = new BitmapText(formatToCommaWithLang(payout), { fontName: 'GodziFont', fontSize: 30 })
      payoutText.anchor.set(0.5, 2.5)
      this.godziSpine.addSlotObject(textSlot, payoutText)
      this.tapHintText = showTapToContinueText(this.godziWrapper, 0, 800)

      const onEndCb = () => {
        this.exitGodziDrop()
      }
      this.godziWrapper.onpointertap = onEndCb
      registerSpaceBarEvent(onEndCb)
    }
    else {
      soundManager.stopBGM()
      soundManager.play(GodziDropSoundTypeV2.betterLuck)

      const animationName = GodziDropAnimation.betterLuckVertical
      await playSpine(this.godziSpine, animationName)
      this.tapHintText = showTapToContinueText(this.godziWrapper, 0, 800)
      this.exitGodziDrop()
    }
  }
}
